import { registerReplies, type ReplyScript } from "../../trainer/replyBriefing.ts";

/**
 * The reply lessons, in packs. A game downloads only the packs written for
 * its side of the board and its first move: an Italian game never needs the
 * closed defences. Packs register in this order, so when two packs brief the
 * same move the earlier one keeps it.
 */
export type ReplyPack =
  | "italian"
  | "caroKann"
  | "defining"
  | "e4-open"
  | "e4-structures"
  | "flank"
  | "black-open"
  | "black-closed";

const PACK_ORDER: ReplyPack[] = [
  "italian",
  "caroKann",
  "defining",
  "e4-open",
  "e4-structures",
  "flank",
  "black-open",
  "black-closed",
];

const loaders: Record<ReplyPack, () => Promise<ReplyScript[]>> = {
  italian: () => import("./italian.ts").then((m) => m.ITALIAN_REPLIES),
  caroKann: () => import("./caroKann.ts").then((m) => m.CARO_REPLIES),
  defining: () => import("./defining.ts").then((m) => m.DEFINING_REPLIES),
  "e4-open": () => import("./e4-open.ts").then((m) => m.E4_OPEN_REPLIES),
  "e4-structures": () => import("./e4-structures.ts").then((m) => m.E4_STRUCTURE_REPLIES),
  flank: () => import("./flank.ts").then((m) => m.FLANK_REPLIES),
  "black-open": () => import("./black-open.ts").then((m) => m.BLACK_OPEN_REPLIES),
  "black-closed": () => import("./black-closed.ts").then((m) => m.BLACK_CLOSED_REPLIES),
};

/**
 * The packs a game needs: the student's side decides who is briefed (a
 * White course briefs Black's replies), and the first move of the course
 * line decides open or closed.
 */
export function packsFor(side: "w" | "b", firstSan: string | null | undefined): ReplyPack[] {
  const first = (firstSan || "").replace(/[+#?!]/g, "");
  if (side === "w") {
    return first === "e4"
      ? ["italian", "defining", "e4-open", "e4-structures"]
      : first
        ? ["defining", "flank"]
        : ["italian", "defining", "e4-open", "e4-structures", "flank"];
  }
  return first === "e4"
    ? ["caroKann", "black-open"]
    : first
      ? ["black-closed"]
      : ["caroKann", "black-open", "black-closed"];
}

const loaded = new Map<ReplyPack, Promise<ReplyScript[]>>();

function loadPack(pack: ReplyPack): Promise<ReplyScript[]> {
  let task = loaded.get(pack);
  if (!task) {
    task = loaders[pack]().catch((error) => {
      loaded.delete(pack);
      throw error;
    });
    loaded.set(pack, task);
  }
  return task;
}

/** Download the named packs (all of them by default) and register their briefings, in pack order. */
export async function loadReplyBriefings(packs: ReplyPack[] = PACK_ORDER): Promise<void> {
  const wanted = PACK_ORDER.filter((pack) => packs.includes(pack));
  const scripts = await Promise.all(wanted.map(loadPack));
  registerReplies(scripts.flat());
}
